import { type JSX, splitProps } from "solid-js";

/**
 * Inline SVG illustrations for empty states — culinary identity, appealing
 * and theme-safe. ALWAYS use `currentColor` and repo color tokens
 * (`text-accent`, `text-muted-foreground`), never fixed hex, adapting
 * to any theme. Decorative: empty state text provides the
 * meaning, so they are marked `aria-hidden`.
 *
 * Pattern: primary stroke inherits `currentColor` (set `text-…` on wrapper
 * or via `class`); soft fills use `fill-accent/10` etc. for depth
 * without noise.
 */
type IllustrationProps = JSX.SvgSVGAttributes<SVGSVGElement> & { class?: string };

function baseProps(props: IllustrationProps) {
  const [, rest] = splitProps(props, ["class"]);
  return {
    viewBox: "0 0 200 160",
    fill: "none",
    role: "img" as const,
    "aria-hidden": "true" as const,
    ...rest,
  };
}

/** Plate and cutlery — for "no recipes". */
export function EmptyRecipesIllustration(props: IllustrationProps) {
  return (
    <svg {...baseProps(props)} class={props.class}>
      {/* plato */}
      <ellipse cx="100" cy="90" rx="58" ry="52" class="fill-accent/10" />
      <circle
        cx="100"
        cy="90"
        r="44"
        class="stroke-current"
        stroke-width="3"
        stroke-opacity="0.55"
      />
      <circle
        cx="100"
        cy="90"
        r="30"
        class="stroke-current"
        stroke-width="2"
        stroke-opacity="0.3"
        stroke-dasharray="4 6"
      />
      {/* tenedor */}
      <g class="stroke-accent" stroke-width="3.5" stroke-linecap="round">
        <line x1="44" y1="40" x2="44" y2="142" />
        <line x1="38" y1="40" x2="38" y2="60" />
        <line x1="50" y1="40" x2="50" y2="60" />
        <path d="M38 60 Q44 70 50 60" fill="none" />
      </g>
      {/* cuchillo */}
      <g class="stroke-accent" stroke-width="3.5" stroke-linecap="round">
        <line x1="156" y1="64" x2="156" y2="142" />
        <path d="M156 40 Q150 52 156 64 Q162 52 156 40Z" class="fill-accent/20" />
      </g>
      {/* vapor */}
      <g class="stroke-current" stroke-width="2.5" stroke-linecap="round" stroke-opacity="0.4">
        <path d="M92 70 q6 -8 0 -16 q-6 -8 0 -16" fill="none" />
        <path d="M108 70 q6 -8 0 -16 q-6 -8 0 -16" fill="none" />
      </g>
    </svg>
  );
}

/** Weekly calendar with cards — for "empty plan". */
export function EmptyPlanIllustration(props: IllustrationProps) {
  return (
    <svg {...baseProps(props)} class={props.class}>
      <rect
        x="34"
        y="36"
        width="132"
        height="100"
        rx="12"
        class="fill-accent/10 stroke-current"
        stroke-width="3"
        stroke-opacity="0.5"
      />
      {/* anillas */}
      <line
        x1="64"
        y1="26"
        x2="64"
        y2="46"
        class="stroke-accent"
        stroke-width="4"
        stroke-linecap="round"
      />
      <line
        x1="136"
        y1="26"
        x2="136"
        y2="46"
        class="stroke-accent"
        stroke-width="4"
        stroke-linecap="round"
      />
      {/* cabecera */}
      <line
        x1="34"
        y1="58"
        x2="166"
        y2="58"
        class="stroke-current"
        stroke-width="3"
        stroke-opacity="0.5"
      />
      {/* tarjetas de comida */}
      <g class="fill-accent/20">
        <rect x="48" y="72" width="40" height="20" rx="5" />
        <rect x="112" y="72" width="40" height="20" rx="5" />
        <rect x="48" y="102" width="40" height="20" rx="5" />
      </g>
      <rect
        x="112"
        y="102"
        width="40"
        height="20"
        rx="5"
        class="stroke-current"
        stroke-width="2"
        stroke-opacity="0.35"
        stroke-dasharray="4 5"
      />
    </svg>
  );
}

/**
 * Skillet with bubbles/spark — for ERROR states (something got "burned").
 * Culinary identity consistent with the rest, theme-safe.
 */
export function ErrorCookingIllustration(props: IllustrationProps) {
  return (
    <svg {...baseProps(props)} class={props.class}>
      {/* sartén */}
      <ellipse cx="92" cy="104" rx="52" ry="20" class="fill-accent/10" />
      <path
        d="M40 100 a52 16 0 0 0 104 0"
        fill="none"
        class="stroke-current"
        stroke-width="3"
        stroke-opacity="0.55"
        stroke-linecap="round"
      />
      <line
        x1="40"
        y1="100"
        x2="144"
        y2="100"
        class="stroke-current"
        stroke-width="3"
        stroke-opacity="0.55"
        stroke-linecap="round"
      />
      {/* mango */}
      <line
        x1="144"
        y1="100"
        x2="186"
        y2="92"
        class="stroke-accent"
        stroke-width="5"
        stroke-linecap="round"
      />
      {/* humo / aviso */}
      <g class="stroke-current" stroke-width="2.5" stroke-linecap="round" stroke-opacity="0.4">
        <path d="M74 90 q8 -12 0 -24 q-8 -12 0 -24" fill="none" />
        <path d="M98 90 q8 -12 0 -24 q-8 -12 0 -24" fill="none" />
      </g>
      {/* chispa de alerta */}
      <g class="stroke-accent" stroke-width="3" stroke-linecap="round">
        <line x1="120" y1="40" x2="120" y2="54" />
        <circle cx="120" cy="64" r="1.5" class="fill-accent stroke-none" />
      </g>
    </svg>
  );
}

/** Shopping basket — for "no lists" / "empty list". */
export function EmptyShoppingIllustration(props: IllustrationProps) {
  return (
    <svg {...baseProps(props)} class={props.class}>
      {/* cesta */}
      <path
        d="M50 70 L150 70 L138 132 Q137 138 131 138 L69 138 Q63 138 62 132 Z"
        class="fill-accent/10 stroke-current"
        stroke-width="3"
        stroke-opacity="0.55"
        stroke-linejoin="round"
      />
      {/* asa */}
      <path
        d="M72 70 Q72 38 100 38 Q128 38 128 70"
        fill="none"
        class="stroke-accent"
        stroke-width="3.5"
        stroke-linecap="round"
      />
      {/* ranuras */}
      <g class="stroke-current" stroke-width="2.5" stroke-opacity="0.35" stroke-linecap="round">
        <line x1="80" y1="84" x2="76" y2="126" />
        <line x1="100" y1="84" x2="100" y2="126" />
        <line x1="120" y1="84" x2="124" y2="126" />
      </g>
      {/* producto que asoma */}
      <circle cx="88" cy="62" r="9" class="fill-accent/30" />
      <path
        d="M88 53 q4 -6 9 -4"
        fill="none"
        class="stroke-accent"
        stroke-width="2.5"
        stroke-linecap="round"
      />
    </svg>
  );
}
